import TextArea from 'components/Common/Form/Input/TextArea'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import type { ChangeEventHandler } from 'react'
import { useCallback, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import {
  RENEWAL_CANCELLATION_REASONS,
  useRenewalCancellationReasonLabels,
  useRenewalCancellationSubmit,
} from './renewalCancellationReasons'

interface Props {
  item: App.MemberSubscriptionItem
  onRenewCancelSuccess: () => void
}

const OptionalLabel = styled.span`
  font-weight: ${(props) => props.theme.font.primary.weight.regular};
`

const messages = defineMessages({
  title: {
    id: 'WSbrhVGwPFKPwOv-0L5k',
    defaultMessage: 'Are you sure you want to cancel your renewal?',
    description: 'Title of the redesigned cancel-renewal survey modal',
  },
  subtitle: {
    id: 'vj8vkELWZwNRELkUYmlf',
    defaultMessage: "Your membership remains active until <b>{date}</b>. After this date, your membership won't renew and you won't be charged again.",
    description: 'Explains when the membership ends after cancelling renewal',
  },
  surveyQuestion: {
    defaultMessage: 'What was the main reason you chose to cancel?',
    id: '83-JZnoYzGV7gW4D7w78',
    description: 'Question label above the optional cancellation reason survey on the cancel renewal modal',
  },
  optional: {
    defaultMessage: '(Optional)',
    id: 'Yu4TMxl16TgsIEmAHcTC',
    description: 'Marks the cancellation reason survey as optional on the cancel renewal modal',
  },
  keepMembership: {
    defaultMessage: 'Keep membership',
    id: 'YzHgFTVcRp04Wy_SpkN1',
    description: 'Primary button to keep the membership and dismiss the cancel renewal survey modal',
  },
  commentPlaceholder: {
    id: '3cHjt-82CnGXW7PfgQym',
    defaultMessage: 'Please specify',
    description: 'Placeholder text for the free-text cancellation reason input',
  },
  cancelRenewal: {
    id: 'ren3ZISQVRnCJt3l-gBM',
    defaultMessage: 'Cancel renewal',
    description: 'Secondary action button to confirm cancellation of renewal',
  },
})

function LuxPlusManageAccountModalRenewalCancellationSurvey({ item, onRenewCancelSuccess }: Props) {
  const intl = useIntl()
  const reasonLabels = useRenewalCancellationReasonLabels()
  const onConfirmUpdateRenewalStatus = useRenewalCancellationSubmit(onRenewCancelSuccess)

  const [selectedReason, setReason] = useState<string | undefined>()
  const handleReasonChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setReason(event.currentTarget.value)
  }, [])

  const fetching = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const selectedOtherReason = !!selectedReason?.startsWith('Other')

  return <form onSubmit={onConfirmUpdateRenewalStatus}>
    <ModalHeader title={intl.formatMessage(messages.title)} />
    <ModalBody>
      <ModalContent>
        <LoadingIndicator floating visible={fetching} />
        <Group direction="vertical" gap={16}>
          <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              {...messages.subtitle}
              values={{
                date: <FormatDateTime value={item.expiryDate} format="dmy-casual" />,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
          <Group direction="vertical" gap={16}>
            <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
              {intl.formatMessage(messages.surveyQuestion)} <OptionalLabel>{intl.formatMessage(messages.optional)}</OptionalLabel>
            </BodyText>
            <Group direction="vertical" gap={16}>
              {RENEWAL_CANCELLATION_REASONS.map((reason) => <RadioInput
                key={reason}
                name="reason"
                value={reason}
                size="medium"
                onChange={handleReasonChange}
              >
                {reasonLabels[reason]}
              </RadioInput>)}
            </Group>
            <Expander expanded={selectedOtherReason}>
              <TextArea
                name="comment"
                required={selectedOtherReason}
                placeholder={intl.formatMessage(messages.commentPlaceholder)}
              />
            </Expander>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ProductPaletteProvider palette="lux-plus">
      <ModalFooter
        // Stack on mobile ("Keep membership" above "Cancel renewal"); side-by-side on tablet+.
        actionsLayout="stacked"
        tabletActionsLayout="auto"
        primaryActionProps={{ children: intl.formatMessage(messages.keepMembership), onClick: dismissModal }}
        secondaryActionProps={{ children: intl.formatMessage(messages.cancelRenewal), type: 'submit' }}
      />
    </ProductPaletteProvider>
  </form>
}

export default LuxPlusManageAccountModalRenewalCancellationSurvey
